import React, { Component } from 'react';
import NotificationsOutlinedIcon from '@material-ui/icons/NotificationsOutlined';
import { Badge, ClickAwayListener, withStyles, IconButton } from '@material-ui/core';
import styles from '../../headerStyles';
import Tooltip from '../../../common/StyledToolTip';
import NotificationPanel from './NotificationPanel/index';
import { inject, observer } from 'mobx-react';
import { compose } from 'recompose';
import withWidth from '@material-ui/core/withWidth';
import Events from '@EventEmitter';
import { APP_EVENT_NAMES } from '../../../../signalR/constants';
import { NOTIFICATIONS_CONTENT } from './content';
import { notificationAudio } from '../../../modules/Dispatch/DispatchChat/ChatSound';
import { buildNavigateToToBeReviewedPayload, handleMessageFromDriverAlert } from './utils';
import type { DriverMessageItem, ToBeReviewedItem } from './types';

const { DISPATCH } = APP_EVENT_NAMES;

const localStyles = (theme: { error?: string; white?: string }) => ({
  unreadCountBadge: {
    height: 15,
    minWidth: 15,
    backgroundColor: theme.error,
    color: theme.white,
  },
  desktopContainer: {
    top: 43,
  },
  tabContainer: {
    top: 45,
    right: -28,
  },
});

type NotificationsProps = {
  classes: Record<string, string>;
  width?: string;
  showNotification?: boolean;
  switchNotificationPanel?: (value?: boolean) => void;
  openDriverChatBox?: (driver: DriverMessageItem['driver']) => void;
  DispatchStore?: {
    unreadNotificationCount: number;
    allUnreadNotifications: Array<DriverMessageItem | ToBeReviewedItem>;
    currentActiveRoutes?: Array<{ driverId?: string }>;
    openChatDriverId?: string | null;
    addDispatchedDriverNotification: (messages: unknown[]) => void;
  };
};

@inject('DispatchStore')
@observer
export class Notifications extends Component<NotificationsProps> {
  componentDidMount() {
    Events.on(DISPATCH.MessageFromDriver, this.onMessageFromDriver);
  }

  componentWillUnmount() {
    Events.off(DISPATCH.MessageFromDriver, this.onMessageFromDriver);
  }

  onMessageFromDriver = (driverMessageString: string) => {
    const { DispatchStore } = this.props;
    if (!DispatchStore) {
      return;
    }

    handleMessageFromDriverAlert(driverMessageString, {
      currentActiveRoutes: DispatchStore.currentActiveRoutes,
      openChatDriverId: DispatchStore.openChatDriverId,
      playSound: () => notificationAudio.play(),
      showToast: (raw) => Events.emit('showToastMessageNotification', raw),
      addNotification: (messages) => DispatchStore.addDispatchedDriverNotification(messages),
    });
  };

  onPickupReviewClick = (
    item: ToBeReviewedItem,
    options: { clearAllActive?: boolean } = {}
  ) => {
    // Dismiss only after Pickups-grid navigation accepts (see GroupPickup).
    Events.emit(
      DISPATCH.NavigateToToBeReviewedPickup,
      buildNavigateToToBeReviewedPayload(item, options)
    );
  };

  openDriverChatBox = (driver: DriverMessageItem['driver']) => {
    this.props.openDriverChatBox?.(driver);
  };

  render() {
    const { classes, width, showNotification, switchNotificationPanel, DispatchStore } =
      this.props;
    const unreadCount = DispatchStore?.unreadNotificationCount ?? 0;
    return (
      <div>
        <div className={`${classes.muiIconContainer} ${classes.headerIconStyles}`}>
          <IconButton
            color="inherit"
            className={classes.muiIconButtonStyle}
            onClick={() => switchNotificationPanel?.()}
            data-automationid="settings-icon"
            aria-label={NOTIFICATIONS_CONTENT.header.tooltip}
          >
            <Tooltip title={NOTIFICATIONS_CONTENT.header.tooltip}>
              <Badge
                badgeContent={unreadCount}
                classes={{ badge: classes.unreadCountBadge }}
                color="primary"
              >
                <NotificationsOutlinedIcon />
              </Badge>
            </Tooltip>
          </IconButton>
        </div>
        {showNotification ? (
          <ClickAwayListener onClickAway={() => switchNotificationPanel?.(false)}>
            <NotificationPanel
              data={DispatchStore?.allUnreadNotifications || []}
              handlers={{
                openDriverChatBox: this.openDriverChatBox,
                onPickupReviewClick: this.onPickupReviewClick,
              }}
              containerStyles={
                ['xl', 'lg'].includes(width || '')
                  ? classes.desktopContainer
                  : classes.tabContainer
              }
            />
          </ClickAwayListener>
        ) : null}
      </div>
    );
  }
}

export default compose(
  withWidth(),
  withStyles(
    (theme) => ({
      ...styles(theme),
      ...localStyles(theme),
    }),
    { withTheme: true }
  )
)(Notifications);
